import { Quote } from '@/model/quotesModel';

import { useMemo } from 'react';

import {
  matchesTab,
  inRange,
  getInflationProtection,
  getBenefitsDuration,
  parseDurationYearsAsNumber,
  matchesInflationType,
  matchesMinimumInflation,
  matchesBenefitPaymentType,
  MAX_DURATION_YEARS,
} from '@/features/shop-insurance/lib/filter-matchers';
import {
  groupQuotesForBenefitDesign,
  reverseInterpolateQuotes,
  reverseInterpolateQuotesForCareStartBenefit,
} from '@/features/shop-insurance/lib/benefit-design-interpolation';
import type {
  BenefitDesignInputs,
  BenefitDesignQuoteGroup,
  CombinedFilterType,
  InflationType,
} from '@/features/shop-insurance/types';
import {
  getBenefitPeriodFromQuote,
  benefitPeriodToString,
  stringToBenefitPeriod,
  LIFETIME_PERIOD,
  type BenefitPeriod,
} from '@/utils/benefitPeriod';
import { enrichQuotesWithROI } from '@shared';
import { getInitialInflationLabel, parseInflationPercent } from '@/utils/inflationParser';
import { getLtcTiming } from '@/features/shop-insurance/lib/ltc-timing';

export type InflationOption = { label: string; rate: number };

export interface BenefitDesignAvailableOptions {
  durationOptions: BenefitPeriod[];
  inflationOptions: InflationOption[];
  aobDurationOptions: BenefitPeriod[];
  cobDurationOptions: BenefitPeriod[];
}

interface UseBenefitDesignQuotesParams {
  originalQuotes: Quote[] | undefined;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  inputs: BenefitDesignInputs;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  /** `inflationProtectionMinimum` flag — picks minimum-% matching over legacy range matching. */
  useMinimumInflation: boolean;
}

interface UseBenefitDesignQuotesResult {
  quoteGroups: BenefitDesignQuoteGroup[];
  availableOptions: BenefitDesignAvailableOptions;
}

function isWildcardInflationRange(range: number[]): boolean {
  return range[0] === 0 && range[1] >= 5;
}

/**
 * Legacy AOB/COB range matching, retained for when `inflationProtectionMinimum` is off.
 * Deletable together with the range UI once the flag is removed.
 */
function matchesInflationRange(quote: Quote, filter: CombinedFilterType): boolean {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;
    const aobSet = !isWildcardInflationRange(filter.aob);
    const cobSet = !isWildcardInflationRange(filter.cob);
    const rangesAreEqual = minAob === minCob && maxAob === maxCob;

    if (!rangesAreEqual || !(aobSet && cobSet)) {
      if (hasSingle && typeof single === 'number') {
        if (aobSet && !cobSet) return inRange(single, minAob, maxAob);
        if (rangesAreEqual) return inRange(single, minAob, maxAob);
        return false;
      }
      return inRange(aob, minAob, maxAob) && inRange(cob, minCob, maxCob);
    }
  }

  const [min, max] = filter.type === 'separate' ? filter.aob : filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, min, max);
  }

  const isHybrid = aob !== cob;

  if (isHybrid) {
    return inRange(aob, min, max) || inRange(cob, min, max);
  }

  return inRange(aob, min, max);
}

function isWildcardRange(range: number[]): boolean {
  return range[0] === 0 && !isFinite(range[1]);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType): boolean {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;

    if (aobYears || cobYears) {
      return (
        inRange(aobYears, minAob, maxAob, MAX_DURATION_YEARS) &&
        inRange(cobYears, minCob, maxCob, MAX_DURATION_YEARS)
      );
    }

    // Quote only has total duration (no separate AOB/COB data — e.g. Nationwide, Securian).
    // A wildcard range [0, Infinity] means that dimension was left empty by the user.
    const aobSet = !isWildcardRange(filter.aob);
    const cobSet = !isWildcardRange(filter.cob);

    if (aobSet && !cobSet) {
      // AOB-only filter: total-duration quotes can match since their entire duration is one phase.
      return inRange(totalYears, minAob, maxAob, MAX_DURATION_YEARS);
    }

    // COB-only or both set: exclude quotes without actual AOB/COB data (only OneAmerica has these).
    return false;
  }

  const [minCombined, maxCombined] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minCombined, maxCombined, MAX_DURATION_YEARS);
  }

  return inRange(totalYears, minCombined, maxCombined, MAX_DURATION_YEARS);
}

function hasDurationData(quote: Quote): boolean {
  return !!(quote.aobDuration || quote.cobDuration || quote.ltcBenefitDurationMonths);
}

function toSortedPeriods(set: Set<string>): BenefitPeriod[] {
  return Array.from(set)
    .map(stringToBenefitPeriod)
    .sort((periodA, periodB) => {
      if (periodA === LIFETIME_PERIOD) return 1;
      if (periodB === LIFETIME_PERIOD) return -1;
      return (periodA as number) - (periodB as number);
    });
}

function toSortedInflationOptions(map: Map<string, number>): InflationOption[] {
  return Array.from(map.entries())
    .sort(([, rateA], [, rateB]) => rateA - rateB)
    .map(([label, rate]) => ({ label, rate }));
}

function extractAvailableOptions(tabFilteredQuotes: Quote[]): BenefitDesignAvailableOptions {
  const durationSet = new Set<string>();
  const aobDurationSet = new Set<string>();
  const cobDurationSet = new Set<string>();
  const inflationMap = new Map<string, number>();

  for (const quote of tabFilteredQuotes) {
    if (hasDurationData(quote)) {
      const period = getBenefitPeriodFromQuote(quote);
      if (period === LIFETIME_PERIOD || (typeof period === 'number' && period > 0)) {
        durationSet.add(benefitPeriodToString(period));
      }
      const aob = parseDurationYearsAsNumber(quote.aobDuration);
      const cob = parseDurationYearsAsNumber(quote.cobDuration);
      if (aob > 0 || aob === Infinity)
        aobDurationSet.add(benefitPeriodToString(aob === Infinity ? LIFETIME_PERIOD : aob));
      if (cob > 0 || cob === Infinity)
        cobDurationSet.add(benefitPeriodToString(cob === Infinity ? LIFETIME_PERIOD : cob));
      if (!quote.aobDuration && !quote.cobDuration && quote.ltcBenefitDurationMonths) {
        const str = benefitPeriodToString(period);
        aobDurationSet.add(str);
        cobDurationSet.add(str);
      }
    }

    const label = getInitialInflationLabel(quote);
    if (label && label !== 'None') {
      const rate = parseInflationPercent(label);
      inflationMap.set(label, rate);
    }
    if (!quote.inflationBenefitOption || quote.inflationBenefitOption === 'None') {
      if (quote.aobInflation && quote.aobInflation !== 'None') {
        inflationMap.set(quote.aobInflation, parseInflationPercent(quote.aobInflation));
      }
      if (quote.cobInflation && quote.cobInflation !== 'None') {
        inflationMap.set(quote.cobInflation, parseInflationPercent(quote.cobInflation));
      }
    }
  }

  return {
    durationOptions: toSortedPeriods(durationSet),
    aobDurationOptions: toSortedPeriods(aobDurationSet),
    cobDurationOptions: toSortedPeriods(cobDurationSet),
    inflationOptions: toSortedInflationOptions(inflationMap),
  };
}

export function useBenefitDesignQuotes({
  originalQuotes,
  client,
  selectedTab,
  inputs,
  returnOnInvestmentCalculationType,
  useMinimumInflation,
}: UseBenefitDesignQuotesParams): UseBenefitDesignQuotesResult {
  const tabFiltered = useMemo(() => {
    if (!originalQuotes?.length) return [];
    return originalQuotes.filter(quote => matchesTab(quote, selectedTab));
  }, [originalQuotes, selectedTab]);

  const availableOptions = useMemo(() => extractAvailableOptions(tabFiltered), [tabFiltered]);

  const quoteGroups = useMemo(() => {
    if (!tabFiltered.length || !client) return [];

    const {
      monthlyBenefit,
      monthlyBenefitTiming,
      benefitDuration,
      inflationProtection,
      inflationProtectionRange,
      inflationTypes,
      benefitPaymentType,
    } = inputs;
    const DEFAULT_MONTHLY_BENEFIT = 5000;
    const effectiveMonthlyBenefit = monthlyBenefit ?? DEFAULT_MONTHLY_BENEFIT;
    const { yearsUntilLtc } = getLtcTiming(
      client.intakeSurvey?.clientAge,
      client.appliedInferenceSet?.ltcAtAge,
    );

    const filtered = tabFiltered.filter(
      quote =>
        (!benefitDuration || matchesBenefitsDuration(quote, benefitDuration)) &&
        (useMinimumInflation
          ? matchesMinimumInflation(quote, inflationProtection ?? 0)
          : !inflationProtectionRange || matchesInflationRange(quote, inflationProtectionRange)) &&
        (!inflationTypes || matchesInflationType(quote, inflationTypes)) &&
        matchesBenefitPaymentType(quote, benefitPaymentType ?? 'all'),
    );

    if (!filtered.length) return [];

    // Care-start targets are back-solved per policy using its own AOB inflation.
    // Policy-issue targets are applied directly, then projected to care start.
    // Quotes where the required premium is out of bounds are excluded.
    const interpolated =
      monthlyBenefitTiming === 'policy-issue'
        ? reverseInterpolateQuotes(filtered, effectiveMonthlyBenefit)
        : reverseInterpolateQuotesForCareStartBenefit(
            filtered,
            effectiveMonthlyBenefit,
            yearsUntilLtc,
          );
    if (!interpolated.length) return [];

    const withROI = enrichQuotesWithROI(interpolated, client, returnOnInvestmentCalculationType, {
      includeOriginalROIValues: true,
    });
    return groupQuotesForBenefitDesign(withROI);
  }, [
    tabFiltered,
    client,
    inputs.monthlyBenefit,
    inputs.monthlyBenefitTiming,
    inputs.benefitDuration,
    inputs.inflationProtection,
    inputs.inflationProtectionRange,
    inputs.inflationTypes,
    inputs.benefitPaymentType,
    useMinimumInflation,
    returnOnInvestmentCalculationType,
  ]);

  return { quoteGroups, availableOptions };
}
